<script setup lang="ts">
import { ref } from 'vue';

import { getHotwordCloudService } from '@/api/dp/index.js';

const props = defineProps<{
  movieId: number;
}>();
const emit = defineEmits<{
  (e: 'loaded'): void;
  (e: 'select', word: string): void;
}>();
interface HotwordItem {
  word: string;
  /** ホットワードの言及数（API の mentionCount） */
  count: number;
}

const wordList = ref<HotwordItem[]>([]);

const getSize = (index: number | string): number => {
  const indexNumber = Number(index);
  if (indexNumber < 1) return 5;
  if (indexNumber < 3) return 4;
  if (indexNumber < 7) return 3;
  if (indexNumber < 12) return 2;
  return 1;
};

/** 言及数をバッジ向けにコンパクト表記へ（1362 → 1.3k、15000 → 15k） */
const formatCount = (count: number): string => {
  if (count >= 10000) return `${Math.floor(count / 1000)}k`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}k`;
  return String(count);
};

const onSelect = (word: string) => {
  if (!word) return;
  emit('select', word);
};

let fetchSeq = 0;

/** 指定映画のホットワードを取得。
 *  親（index.vue）が /dp/scheduleCount 完了後に呼び出す。連打や映画切替で
 *  古いリクエストの結果は破棄し、最新の呼び出しのレスポンスだけを反映する。 */
const fetchHotwords = () => {
  if (!props.movieId) {
    wordList.value = [];
    return;
  }
  // 映画切り替え時に前の映画のホットワードが残らないようクリア
  wordList.value = [];
  const seq = ++fetchSeq;
  getHotwordCloudService({
    movieId: props.movieId,
    // count: 19,
  })
    .then((res: any) => {
      // より新しい movieId のリクエストが発行済みなら破棄
      if (seq !== fetchSeq) return;
      const list: any[] = Array.isArray(res)
        ? res
        : Array.isArray((res as any)?.data)
          ? (res as any).data
          : [];
      wordList.value = list.map((raw: any) => ({
        word: String(raw?.keyword ?? raw?.word ?? raw?.name ?? raw ?? ''),
        count: Number(raw?.mentionCount ?? raw?.count ?? 0) || 0,
      }));
      // 描画後に親へ通知し、ヒートスケール凡例をページ実色から再構築させる
      emit('loaded');
    })
    .catch(() => {
      if (seq !== fetchSeq) return;
      wordList.value = [];
    });
};

// 親が /dp/scheduleCount 完了後に呼び出すため公開。
// movieId 変化だけで先行取得せず、scheduleCount の後に呼ぶ（親がタイミングを制御）。
defineExpose({ fetchHotwords });
</script>
<template>
  <div class="word-cloud">
    <span
      class="word-tag"
      v-for="(item, index) in wordList"
      :key="index"
      :class="`word-tag-size-${getSize(index)}`"
      @click="onSelect(item.word)"
    >
      {{ item.word }}
      <span class="word-tag-count" v-if="item.count > 0">{{ formatCount(item.count) }}</span>
    </span>
  </div>
</template>
<style lang="less" scoped>
  .word-cloud {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: center;
    justify-content: center;
    padding: 20px;
    min-height: 200px;
    .word-tag {
      display: inline-block;
      padding: 8px 16px;
      border-radius: 20px;
      font-weight: 600;
      position: relative;
      transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.25s ease;
      cursor: pointer;
      animation: wordFadeIn 0.5s ease-out forwards;
      opacity: 0;
      &:hover {
        transform: translateY(-5px) scale(1.08);
        box-shadow: 0 10px 20px rgba(0, 0, 0, 0.18);
        z-index: 10;
      }
      .word-tag-count {
        position: absolute;
        top: -8px;
        right: -8px;
        min-width: 20px;
        height: 20px;
        padding: 0 6px;
        border-radius: 10px;
        background: #ff3b30;
        color: #ffffff;
        font-size: 12px;
        font-weight: 700;
        line-height: 20px;
        text-align: center;
        box-shadow: 0 2px 6px rgba(255, 59, 48, 0.5);
        border: 2px solid var(--bg-panel);
      }
      &.word-tag-size-1 {
        font-size: 0.9rem;
        background: rgba(107, 114, 128, 0.2);
        color: rgb(107, 114, 128);
      }
      &.word-tag-size-2 {
        font-size: 1.1rem;
        background: rgba(22, 163, 74, 0.2);
        color: rgb(22, 163, 74);
      }
      &.word-tag-size-3 {
        font-size: 1.3rem;
        background: rgba(234, 88, 12, 0.2);
        color: rgb(234, 88, 12);
      }
      &.word-tag-size-4 {
        font-size: 1.5rem;
        background: rgba(236, 72, 153, 0.2);
        color: var(--accent-pink);
      }
      &.word-tag-size-5 {
        font-size: 1.7rem;
        background: rgba(220, 38, 38, 0.2);
        color: rgb(220, 38, 38);
      }
      &:nth-child(1) {
        animation-delay: 0.1s;
      }
      &:nth-child(2) {
        animation-delay: 0.15s;
      }
      &:nth-child(3) {
        animation-delay: 0.2s;
      }
      &:nth-child(4) {
        animation-delay: 0.25s;
      }
      &:nth-child(5) {
        animation-delay: 0.3s;
      }
      &:nth-child(6) {
        animation-delay: 0.35s;
      }
      &:nth-child(7) {
        animation-delay: 0.4s;
      }
      &:nth-child(8) {
        animation-delay: 0.45s;
      }
      &:nth-child(9) {
        animation-delay: 0.5s;
      }
      &:nth-child(10) {
        animation-delay: 0.55s;
      }
      &:nth-child(11) {
        animation-delay: 0.6s;
      }
      &:nth-child(12) {
        animation-delay: 0.65s;
      }
      &:nth-child(13) {
        animation-delay: 0.7s;
      }
      &:nth-child(14) {
        animation-delay: 0.75s;
      }
      &:nth-child(15) {
        animation-delay: 0.8s;
      }
      &:nth-child(16) {
        animation-delay: 0.85s;
      }
      &:nth-child(17) {
        animation-delay: 0.9s;
      }
      &:nth-child(18) {
        animation-delay: 0.95s;
      }
      &:nth-child(19) {
        animation-delay: 1.0s;
      }
    }
    
  }

  @keyframes wordFadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
</style>